import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import type { FlightItineraryDetailPageProps } from "@b2bportal/core-flights";
import { useFlightItineraryDetailPage } from "@b2bportal/core-flights";
import { useModuleBEM } from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TripsAppPage } from "@tenant/ui";
import clsx from "clsx";
import { TripDetailContent } from "./components";
import styles from "./styles.module.scss";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import { FlightReservationExchange } from "@/modules/flights-postbooking/components";
import { PageLoadingScreen } from "@/overrides/core-ui/PageLoadingScreen/PageLoadingScreen";

const COMPONENT_KEY = CoreFlightsComponent.FlightItineraryDetailPage;

export const FlightItineraryDetailPage = ({
  tripsRoute,
  className,
  children,
}: FlightItineraryDetailPageProps) => {
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();
  const navigate = useNavigate();
  const [showExchange, setShowExchange] = useState(false);
  const { context, state } = useFlightItineraryDetailPage({ tripsRoute });
  const { isExperimentVariant } = useExperiment();
  const isExchangeEnabled = isExperimentVariant(
    Experiments.EXCHANGE_ENABLED.name,
    Experiments.EXCHANGE_ENABLED.variants.available
  );

  const navigateToCancellation = useCallback(() => {
    navigate("cancel");
  }, [navigate]);

  return (
    <TripsAppPage
      className={clsx(block, className)}
      contentClassName={cn("content")}
    >
      {state.isLoading ? (
        <PageLoadingScreen
          className={cn("loading")}
          isLoading
          text={t("fetchingTrips")}
        />
      ) : context != null ? (
        showExchange ? (
          <FlightReservationExchange
            itinerary={context.itinerary}
            isEnabled={isExchangeEnabled}
            onBack={() => setShowExchange(false)}
          />
        ) : (
          <TripDetailContent
            context={context}
            onQueryCancellation={navigateToCancellation}
            onQueryExchange={() => setShowExchange(true)}
          >
            {children}
          </TripDetailContent>
        )
      ) : null}
    </TripsAppPage>
  );
};
